import { md5 } from '~/util/md5';
import type OrderItem from '~/models/order';

declare global {
  interface Window {
    ADT?: {
      Tag?: {
        t?: number,
        c?: string,
        tp?: number,
        am?: number | string,
        ti?: string,
        xd?: string,
        cpn?: string,
        doEvent?: () => void,
      },
    };
  }
}

// AdTraction was unable to reliably pick up purchase tracking when the
// conversion tag was fired from GTM, so it's sent directly from the client instead.
// The AdTraction tag script itself is loaded globally on every page by
// plugins/adtraction.client.ts, as required by AdTraction's SPA setup guide
// (see https://adtraction.com/subdomain.htm?tp=1954028304).
const AD_TRACTION_TP = 1954028304;

/**
 * Waits for the globally loaded AdTraction tag script to expose
 * window.ADT.Tag.doEvent, which is required to fire a purchase event
 * outside of a normal full page load (SPA behaviour).
 */
const waitForAdTractionTag = async(maxWaitMs = 5000): Promise<boolean> => {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    if (typeof window.ADT?.Tag?.doEvent === 'function') {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return false;
};

/**
 * Hashes an email address with MD5, as required by AdTraction's xd field.
 * Email must be trimmed, lowercased and UTF-8 encoded before hashing.
 */
const hashEmail = (email?: string): string | undefined => {
  if (!email) {
    return undefined;
  }

  return md5(email.toLowerCase().trim());
};

export default function useAdTractionConversion() {
  /**
   * Send AdTraction conversion tracking for a purchase by populating
   * window.ADT.Tag and calling doEvent(), instead of relying on the
   * GTM-based conversion tag.
   */
  const sendPurchaseConversion = async(order: OrderItem, currency?: string) => {
    try {
      const tagReady = await waitForAdTractionTag();
      if (!tagReady) {
        console.warn('AdTraction tag script not ready, skipping purchase conversion');
        return;
      }

      // Order value excluding VAT and shipping, after discounts, per AdTraction's requirements.
      // grandTotal and shippingPrice are both VAT-inclusive customer prices, so shipping's
      // own VAT must be stripped out separately before subtracting it from the ex-VAT total,
      // otherwise shipping VAT ends up subtracted twice.
      const totalExTax = order.totalPriceAsNumber - order.taxAddedAsNumber;
      const shippingExTax = order.shippingPriceAsNumber / (1 + (order.taxPercent || 0) / 100);
      const orderValueExTaxAndShipping = Math.round((totalExTax - shippingExTax) * 100) / 100;
      const email = order.address?.email ?? order.shippingAddress?.email;

      window.ADT = window.ADT || {};
      window.ADT.Tag = window.ADT.Tag || {};
      window.ADT.Tag.t = 3;
      window.ADT.Tag.c = currency || order.currency;
      window.ADT.Tag.tp = AD_TRACTION_TP;
      window.ADT.Tag.am = orderValueExTaxAndShipping;
      window.ADT.Tag.ti = order.orderId;
      window.ADT.Tag.xd = hashEmail(email);
      window.ADT.Tag.cpn = order.voucher || '';

      window.ADT.Tag.doEvent?.();

      console.log('AdTraction purchase conversion sent:', window.ADT.Tag);
    } catch (error) {
      console.error('Failed to send AdTraction purchase conversion:', error);
    }
  };

  return {
    sendPurchaseConversion,
  };
}
